import React from 'react'
import { Plural } from '@lingui/macro'
import { AnalyticsContext, AnalyticsFilterType } from '../../AnalyticsContext'
import { ShowFilterModalButton } from './ShowFilterModalButton'
import { SearchableEntity } from '../../../../components/common/selectEntitiesForm'
import { AnalyticsFiltersQuery } from './AnalyticsFilter.codegen'
import { getTitleForResource } from 'front-end/src/components/common/selectEntitiesForm/utils'
import { AnalyticsFiltersEnum } from '../../constants'
import { ModalFilterEntitiesEnum } from '../../../../components/common/entityFiltersModals/EntityFilterModalControlProvider'

type SelectedFiltersProps = {
  activeFilters: Array<AnalyticsFiltersEnum>
  data: AnalyticsFiltersQuery
  customLoadedEntities?: Partial<
    Record<AnalyticsFiltersEnum, SearchableEntity[] | null>
  >
}

const getResourceName = (
  filters: AnalyticsFilterType,
  filterKey: 'workspaceIds' | 'pathwayIds' | 'groupIds' | 'campaignIds',
  resources: SearchableEntity[]
): string => {
  const firstSelectedResourceId = filters[filterKey]?.[0]
  if (!firstSelectedResourceId) {
    return ''
  }

  const resource = resources.find((item) => item.id === firstSelectedResourceId)
  if (resource) {
    return getTitleForResource(resource)
  }
  return ''
}

export const SelectedFilters: React.FC<SelectedFiltersProps> = ({
  activeFilters,
  customLoadedEntities,
  data
}) => {
  const { filter } = React.useContext(AnalyticsContext)

  const groupName = React.useMemo(
    () =>
      getResourceName(
        filter,
        'groupIds',
        (data?.groups as SearchableEntity[]) ?? []
      ),
    [filter.pathwayIds, data?.pathways.items]
  )
  const workspaceName = React.useMemo(
    () =>
      getResourceName(
        filter,
        'workspaceIds',
        (data?.workspacesForAnalytics as SearchableEntity[]) ?? []
      ),
    [filter.pathwayIds, data?.pathways.items]
  )

  const pathwayName = React.useMemo(
    () =>
      getResourceName(
        filter,
        'pathwayIds',
        (data?.pathways.items as SearchableEntity[]) ?? []
      ),
    [filter.pathwayIds, data?.pathways.items]
  )

  const campaignName = React.useMemo(
    () =>
      getResourceName(
        filter,
        'campaignIds',
        (customLoadedEntities?.[
          AnalyticsFiltersEnum.CAMPAIGN_FILTER
        ] as SearchableEntity[]) ?? []
      ),
    [
      filter.campaignIds,
      customLoadedEntities?.[AnalyticsFiltersEnum.CAMPAIGN_FILTER]
    ]
  )

  return (
    <>
      <ShowFilterModalButton
        modalId={ModalFilterEntitiesEnum.WORKSPACE}
        filterKey={'workspaceIds'}
        filterId={AnalyticsFiltersEnum.WORKSPACE_FILTER}
        activeFilters={activeFilters}
      >
        <Plural
          value={filter.workspaceIds?.length}
          one={`Workspace: ${workspaceName}`}
          other="Workspaces: # workspaces"
        />
      </ShowFilterModalButton>

      <ShowFilterModalButton
        modalId={ModalFilterEntitiesEnum.PATHWAY}
        filterKey={'pathwayIds'}
        activeFilters={activeFilters}
        filterId={AnalyticsFiltersEnum.PATHWAY_FILTER}
      >
        <Plural
          value={filter.pathwayIds?.length}
          one={`Pathway: ${pathwayName}`}
          other="Pathways: # pathways"
        />
      </ShowFilterModalButton>

      <ShowFilterModalButton
        modalId={ModalFilterEntitiesEnum.GROUP}
        filterKey={'groupIds'}
        filterId={AnalyticsFiltersEnum.GROUP_FILTER}
        activeFilters={activeFilters}
      >
        <Plural
          value={filter.groupIds?.length}
          one={`Group: ${groupName}`}
          other="Groups: # groups"
        />
      </ShowFilterModalButton>
      <ShowFilterModalButton
        modalId={ModalFilterEntitiesEnum.CAMPAIGN}
        filterKey={'campaignIds'}
        filterId={AnalyticsFiltersEnum.CAMPAIGN_FILTER}
        activeFilters={activeFilters}
      >
        <Plural
          value={filter.campaignIds?.length}
          one={
            campaignName ? `Campaign: ${campaignName}` : `Campaign: 1 campaign`
          }
          other="Campaigns: # campaigns"
        />
      </ShowFilterModalButton>
    </>
  )
}
